import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Lock, ArrowRight, Sparkles, Upload, ClipboardCheck, FileText, Target } from 'lucide-react';
import { GCU_STEPS, gcuStepNumber, type GcuStep } from '@klinik/shared';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useAuth } from '../lib/auth';
import { fadeOnly, fadeRise, staggerContainer } from '../lib/motion';

const STEP_LABEL: Record<GcuStep, string> = {
  identity_pic: 'Identitas & PIC',
  upload_documents: 'Upload data awal',
  draft_pic_questionnaire: 'Draft kuesioner PIC',
  send_pic_questionnaire: 'Kirim kuesioner PIC',
  collect_pic_answers: 'Kumpulkan jawaban PIC',
  draft_owner_questionnaire: 'Draft verifikasi Owner',
  collect_owner_answers: 'Verifikasi Owner',
  generate_report: 'Laporan GCU',
  completed: 'Selesai',
};

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const variants = reduce ? fadeOnly : fadeRise;
  const isInternal = user?.role === 'coach' || user?.role === 'admin';

  return (
    <AppShell active="dashboard">
      <motion.div initial="hidden" animate="show" variants={staggerContainer}>
        {/* Welcome banner (gradient — exception #5) */}
        <motion.div
          variants={variants}
          className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-blue-700 via-blue-600 to-sky-400 p-5 text-white shadow-soft"
        >
          <div>
            <div className="font-display text-xl font-bold">
              Selamat datang, {user?.name?.split(' ')[0]}
            </div>
            <div className="mt-0.5 text-sm text-white/85">
              {isInternal
                ? 'Konsol internal Nine Co. Admin console lengkap hadir pada CR6.'
                : 'Mulai General Check-Up bisnis Anda. Tahap berikutnya: unggah data awal.'}
            </div>
          </div>
          {isInternal ? (
            <Badge tone="done">Konsol internal</Badge>
          ) : (
            <Button
              onClick={() => navigate('/intake/upload')}
              className="bg-white text-brand hover:bg-white/90"
              icon={<Upload size={15} aria-hidden />}
            >
              Unggah Data Awal
            </Button>
          )}
        </motion.div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <motion.div variants={variants} className="lg:col-span-2">
            <Card>
              <div className="mb-3 flex items-center justify-between">
                <div className="font-display text-base font-bold text-slate-900">
                  Progres General Check-Up
                </div>
                <Badge tone="progress">{GCU_STEPS.length} langkah</Badge>
              </div>
              <div className="space-y-2.5">
                {GCU_STEPS.map((step) => {
                  const n = gcuStepNumber(step);
                  const done = step === 'identity_pic'; // CR1 establishes step 1
                  // Step 2 (upload) is the current actionable step for owners (CR2).
                  const active = step === 'upload_documents' && !isInternal;
                  return (
                    <div key={step} className="flex items-center gap-3">
                      <span
                        className={`grid h-7 w-7 place-items-center rounded-full text-xs font-bold ${
                          done
                            ? 'bg-green-600 text-white'
                            : active
                              ? 'bg-brand text-white'
                              : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {done ? <Check size={13} /> : n}
                      </span>
                      <div
                        className={`flex-1 text-sm ${
                          done || active ? 'font-medium text-slate-700' : 'text-slate-400'
                        }`}
                      >
                        {STEP_LABEL[step]}
                      </div>
                      {done ? (
                        <Badge tone="done">Siap</Badge>
                      ) : active ? (
                        <button
                          onClick={() => navigate('/intake/upload')}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                        >
                          Mulai <ArrowRight size={13} aria-hidden />
                        </button>
                      ) : (
                        <Lock size={15} className="text-slate-300" aria-label="Terkunci" />
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </motion.div>

          <motion.div variants={variants} className="space-y-4">
            {!isInternal && (
              <Card>
                <div className="mb-2 flex items-center gap-2 text-blue-600">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-blue-50">
                    <ClipboardCheck size={18} aria-hidden />
                  </span>
                  <span className="text-sm font-semibold text-slate-800">Verifikasi Owner</span>
                </div>
                <p className="text-xs text-slate-500">
                  Setelah jawaban PIC terkumpul, konfirmasi temuannya di kuesioner verifikasi Owner
                  (ronde 2).
                </p>
                <button
                  onClick={() => navigate('/verify')}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  Buka verifikasi <ArrowRight size={14} aria-hidden />
                </button>
              </Card>
            )}
            {!isInternal && (
              <Card>
                <div className="mb-2 flex items-center gap-2 text-blue-600">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-blue-50">
                    <FileText size={18} aria-hidden />
                  </span>
                  <span className="text-sm font-semibold text-slate-800">Laporan GCU</span>
                </div>
                <p className="text-xs text-slate-500">
                  Setelah analisa selesai, laporan kesehatan bisnis Anda (skor + SWOT + rekomendasi)
                  akan tersedia di sini. Kami beri tahu lewat email saat siap.
                </p>
                <button
                  onClick={() => navigate('/report')}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  Lihat laporan <ArrowRight size={14} aria-hidden />
                </button>
              </Card>
            )}
            {!isInternal && (
              <Card>
                <div className="mb-2 flex items-center gap-2 text-violet-600">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-violet-50">
                    <Target size={18} aria-hidden />
                  </span>
                  <span className="text-sm font-semibold text-slate-800">Coaching</span>
                </div>
                <p className="text-xs text-slate-500">
                  Setelah laporan GCU difinalkan, program coaching yang sesuai akan direkomendasikan
                  di sini. Pilih program & pantau progres sesi serta perbaikan skor.
                </p>
                <button
                  onClick={() => navigate('/coaching')}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                >
                  Buka coaching <ArrowRight size={14} aria-hidden />
                </button>
              </Card>
            )}
            <Card>
              <div className="mb-2 flex items-center gap-2 text-violet-600">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-violet-50">
                  <Sparkles size={18} aria-hidden />
                </span>
                <span className="text-sm font-semibold text-slate-800">Pipeline AI siap</span>
              </div>
              <p className="text-xs text-slate-500">
                Fondasi RAG (pgvector + HNSW, terisolasi per perusahaan) sudah disiapkan. Mulai aktif
                saat upload dokumen di CR2–CR3.
              </p>
            </Card>
            <Card>
              <div className="mb-1 text-sm font-semibold text-slate-800">Akun Anda</div>
              <div className="text-xs text-slate-500">
                Peran: <b className="text-slate-700">{user?.role}</b>
              </div>
              {!isInternal && (
                <a
                  href="/onboarding"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand"
                >
                  Kelola profil &amp; PIC <ArrowRight size={14} />
                </a>
              )}
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </AppShell>
  );
}
